import { useTranslation } from "react-i18next";
import { SETTING_FILE_TYPES } from "./setting-file-types.js";
import { unityEditorLaunchUrl, unityTestBuildUrl, unityTestKeycloak, unityTestStageUrl } from "../webgl-panel/unity-launch.js";
import { createUnityTokenSource } from "../webgl-panel/unity-token.js";
import type { WebglSignInFailure } from "../../../auth/webgl-popup-auth.js";
import { startEmbedHost, type EmbedErrorCode, type EmbedHost, type EmbedRect } from "../webgl-panel/embed-host.js";
import { getEmbedToken } from "../../../auth/keycloak.js";

import { readBootstrap } from "../../../tenant/bootstrap.js";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, ChevronLeft, ChevronUp, Eye, Folder, Loader2, Move, Plus, RefreshCw, Settings, SlidersHorizontal } from "lucide-react";
import { Dialog, Modal, ModalOverlay } from "react-aria-components";
import { AllStepsCard } from "./AllStepsCard.js";
import { BlueprintPickerDrawer } from "./BlueprintPickerDrawer.js";
import type { AuthSession } from "../../../auth/keycloak.js";
import { Button } from "@/components/ui/button";
import { BackgroundJobs } from "@/components/header/BackgroundJobs";
import { ThemeToggle } from "@/components/header/ThemeToggle";
import { SearchField } from "@/components/ui/search-field";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { ManualBody, ManualBodySection } from "@arc/contracts";
import { ManualViewer } from "../manual-viewer/ManualViewer.js";
import { useManualBody, useManualBodyMutations } from "../useManualMutations.js";
import { OutlineEditorPanel } from "./OutlineEditorPanel.js";
import { EditorStatusControl } from "./EditorStatusControl.js";
import { ElementInfoProvider } from "./element-info.js";
import { opensAllSteps, stepsForQuery } from "./outline-order.js";
import { LazyMount } from "./LazyMount.js";
import { sectionsForStep } from "./body-sections.js";
import { fileMatches, useSideDrop, type DroppedFile, type SideDrag } from "./side-drag.js";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { InsertSlot, RenameInput, SectionActionsMenu, StepActionsMenu, createSectionAt, createStepAt, seedFirstStep } from "./outline-controls.js";
import { STEP_TYPE_ICON } from "./step-type-icon.js";
import { clearNewManualMark, hasNewManualMark } from "./seed-mark.js";
import { StepDots } from "./StepDots.js";
import { StepCard } from "./StepCard.js";
import { ALL_STEPS_KEY, firstStepSensorMap } from "./sensor-setting-model.js";
import { useBodyWriteGate, type BodyWriteGate } from "./useBodyWriteGate.js";
import { useManualLock } from "./useManualLock.js";
import { useMediaJobs } from "@/realtime/useMediaJobs.js";
import { useMe } from "../../../api/useMe.js";
import { ManualSettingsDialog } from "../ManualSettingsDialog.js";
import { ManualLockedNotice } from "./ManualLockedNotice.js";
import { PublishedManualGate } from "./PublishedManualGate.js";
import { AssetLibraryDrawer } from "../asset-library/AssetLibraryDrawer.js";
import type { AssetFile } from "@arc/contracts";

/** Image + video extensions the step-media picker accepts (legacy `GetMediaFileEndings`). */
const MEDIA_TYPES = [".jpg", ".jpeg", ".png", ".svg", ".mp4", ".mov", ".m4v"];
/** 3D model EXTENSIONS — the asset drawer filters by filename suffix (`matchesTypes`), never by AssetKind. Passing
 *  the kind `"model3d"` matched nothing at all, so every model row came back `selectable: false` and no 3D object
 *  could be attached (diff-panel R1 High #1). Mirrors `mediaKind`'s own model list in the API. */

/** Body-write failures that are SURFACED + non-divergent — a duplicate name or deleting the only section: the target simply
 *  keeps its prior value and a dismissible inline banner explains it. Unlike a stale-family 409 (which needs a Refresh/reseed)
 *  these carry NO unsaved edit to lose, so the write gate must NOT latch its failure barrier on them — else exiting or a
 *  status transition would wedge until a page reload (panel diff-a1-round2 Medium). Shared by the gate classifier + the banner. */
export const RECOVERABLE_WRITE_CODES = new Set([
  "BODY_NAME_CONFLICT",
  "BODY_LAST_SECTION",
  // The AR element writes (diff-panel AR-P3d Medium #1). Each surfaces its OWN scoped banner in the step card, and
  // neither diverges the body: a rejected add wrote nothing, and a rejected remove means the element is ALREADY gone
  // (`STEP_NOT_FOUND` is what a double-click gets). Left out of this set they latch the gate's unacked flag, and the
  // editor then silently refuses to exit or switch section — a wedge with no visible cause.
  "STEP_NOT_FOUND",
  "BLUEPRINT_UNAVAILABLE",
  // A setting write / paste the server refused by SHAPE (diff-panel R3 High #1). Nothing diverged — the target keeps
  // its prior value — and each mutation surfaces its own step-scoped banner. Left out, the 422 latched the gate and
  // the editor then refused to exit or switch section with nothing on screen to say why.
  "SETTING_UNWRITABLE",
]);

/**
 * The outline banner's text, per code — SEPARATE from the set above (diff-panel R2 Medium #2).
 *
 * These were one set, and the banner fell through to "That name is already used" for anything that was not
 * `BODY_LAST_SECTION`. Adding the two AR codes to the recoverable set therefore made a failed add/remove announce a
 * duplicate-name conflict that never happened — and did the same to `setStepMainElement` / `changeStepType`, which
 * throw `BLUEPRINT_UNAVAILABLE`. A MAP is the fix, not a longer set: a code with no entry here simply shows no banner,
 * so the next code added to the recoverable set cannot silently inherit somebody else's message. The AR writes are
 * absent on purpose — each already raises its own message in the step card that failed.
 */
const OUTLINE_CONFLICT_KEYS = {
  BODY_NAME_CONFLICT: "manualEditor.outlineError.nameConflict",
  BODY_LAST_SECTION: "manualEditor.outlineError.lastSection",
} as const;

/** The outline banner for the first failure that HAS a message — `null` when none does (so no banner, never a wrong one). */
export function outlineConflict(
  errors: Array<{ code?: string } | null | undefined>,
): { code: string; messageKey: (typeof OUTLINE_CONFLICT_KEYS)[keyof typeof OUTLINE_CONFLICT_KEYS] } | null {
  for (const e of errors) {
    const code = e?.code;
    if (code != null && code in OUTLINE_CONFLICT_KEYS) {
      return { code, messageKey: OUTLINE_CONFLICT_KEYS[code as keyof typeof OUTLINE_CONFLICT_KEYS] };
    }
  }
  return null;
}

/**
 * The authoring editor (ADR-002 Phase 10 — the **content subset**) — a full-screen sibling of {@link ManualViewer}, opened
 * from the manuals view's Edit button (`?arc_edit=<id>`). Layout (owner 2026-11-02): one header with the tools, the outline
 * at the far left folded to a column of dots, and the main column with every section's steps, of which one is open as a
 * **card**. It reads the SAME `GET /manuals/:id/body` (the #173 gate lets a
 * confirm-tier / assigned reviewer read a draft) and writes via the fine-grained Phase-9 commands, each carrying the loaded
 * `manual.version` as the optimistic CAS. Editing is enabled only for a `draft` manual (the server re-checks every command);
 * a non-draft renders read-only with the status control offering the way back. Geometry / AR elements / media attachment
 * are the frozen Unity shadow — surfaced read-only elsewhere, not authored here.
 */
const OUTLINE_OPEN_KEY = "arc_editor_outline_open";
function readOutlineOpen(): boolean {
  try {
    return window.localStorage.getItem(OUTLINE_OPEN_KEY) === "true";
  } catch {
    return false;
  }
}

export function ManualEditor({
  session,
  manualId,
  onClose,
  onExitToFacility,
}: {
  session: AuthSession | null;
  manualId: number;
  onClose: () => void;
  onExitToFacility?: (facilityId: number) => void;
}) {
  const { t, i18n } = useTranslation();
  const body = useManualBody(session, manualId);
  const me = useMe(session);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const m = useManualBodyMutations(session, manualId);

  // Pending text-save flushers registered by each StepCard. Flushed before any lifecycle boundary (section switch / exit /
  // status transition) so an in-flight debounced autosave is never dropped (panel High). Stable identities so the child
  // effect doesn't churn. `reloadKey` remounts the cards to DISCARD local drafts on a conflict-refresh (panel Critical).
  const flushers = useRef(new Set<() => Promise<void>>());
  const registerFlush = useCallback((fn: () => Promise<void>) => flushers.current.add(fn), []);
  const unregisterFlush = useCallback((fn: () => Promise<void>) => flushers.current.delete(fn), []);
  const isRefreshing = useRef(false);
  const [reloadKey, setReloadKey] = useState(0);
  // `refreshing` gates EDITING while a conflict-refresh's authoritative refetch is in flight — otherwise a field made dirty in
  // that sub-second window (on still-stale content) would ride the freshly-reseeded CAS and silently clobber the concurrent
  // author (panel diff-a1 Critical). Cleared once the fresh body is loaded + the cards remount on it.
  const [refreshing, setRefreshing] = useState(false);
  // Clear the discard flag AFTER a conflict-refresh remount commits (the old cards' unmount-flush was suppressed).
  useEffect(() => {
    isRefreshing.current = false;
  }, [reloadKey]);

  const [outlineOpen, setOutlineOpenState] = useState(readOutlineOpen);
  const setOutlineOpen = (open: boolean) => {
    setOutlineOpenState(open);
    try {
      window.localStorage.setItem(OUTLINE_OPEN_KEY, String(open));
    } catch {
      // A blocked storage only costs the memory across visits.
    }
  };
  const [query, setQuery] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  /** The legacy "Alle Schritte" view (ADR-028) — the main pane shows the manual-wide element card instead of a section. */
  const [viewAllSteps, setViewAllSteps] = useState(false);
  const [focusStepId, setFocusStepId] = useState<number | null>(null);
  /** Bumped on every outline click, so clicking the same step again scrolls back to it after the author scrolled away. */
  const [focusTick, setFocusTick] = useState(0);
  /** The one step open as a card (owner 2026-11-02), and so the one the outline highlights. `null`: all folded. */
  const [openStepId, setOpenStepId] = useState<number | null>(null);
  const [stepDragActive, setStepDragActive] = useState(false);
  const [overwrite, setOverwrite] = useState<{ name: string; run: () => void } | null>(null);
  // Asset library (ADR-024) — a right-side OVERLAY drawer over the editor (it never pushes the editor). `manage` opens it from
  // the rail (browse/preview); `pick` opens it from a step's Add/Replace-media (a target step → on select, attach).
  const [assetDrawer, setAssetDrawer] = useState<{ mode: "manage" | "pick"; stepId: number | null } | null>(null);

  const data = body.data;
  const version = data?.manual.version ?? 0;
  // The editor-wide serialized write gate (panel #210): ALL body writes (step autosave + rename/type/payload + outline ops +
  // attach) enqueue through it, so they never race the shared `manual.version` into a self-inflicted 409, and a failed write
  // rejects — giving the flush barrier below teeth. Fed the live query version; it advances the CAS token on its own writes.
  const gate = useBodyWriteGate(version, (err) => {
    const code = (err as { code?: string } | null)?.code;
    return code != null && RECOVERABLE_WRITE_CODES.has(code);
  });
  const settleWrites = gate.settle;
  // AWAITABLE barrier before a lifecycle boundary (exit / section switch / status transition): force any pending debounced
  // text buffers to enqueue NOW, then await the WHOLE gate queue (text + structural + attach) and PROPAGATE a failure — so the
  // boundary BLOCKS on any unsaved/conflicted body write (panel #210 High #1/#2), then the Refresh banner surfaces it.
  const flushAll = useCallback(async () => {
    [...flushers.current].forEach((f) => void f().catch(() => {}));
    await settleWrites();
  }, [settleWrites]);
  // Editability (ADR-018 amendment — legacy parity): the server allows editing ANY non-archived, non-linked manual; `canEdit`
  // (from the body capabilities) already encodes the authz. A PUBLISHED (confirmed/marked) manual is editable too, but we warn
  // once first (the legacy `editManualConfirmation` dialog — edits apply live to the released version).
  const canEdit = data?.capabilities.canEdit ?? false;
  const canConfirm = data?.capabilities.canConfirm ?? false;
  // Only AR manuals carry a main-element type (the legacy Auswählen picker) — a non-AR/document manual is text-only, so the
  // Type picker is hidden there (legacy parity; 699 of 784 Takeda manuals are non-AR).
  // The CONTENT OWNER's flag, not `data.manual.isArManual` (diff-panel AR P2): a linked child serves its parent's
  // body and may itself be flagged non-AR, and gating on the child's own scalar hid the element strip on a body that
  // actually has elements. The server already resolves this — `capabilities.isArBody`.
  const isArManual = data?.capabilities.isArBody ?? false;
  const allStepsOpen = opensAllSteps(data?.capabilities.platforms);
  const isPublished = data ? data.manual.state === "confirmed" || data.manual.state === "marked" : false;
  const [ackPublished, setAckPublished] = useState(false);
  /** The step whose main element is being chosen (the legacy `Auswählen`). Only the STEP id is held: the selected tile
   *  is derived from the live body at render time, so reopening the drawer while a write is still in flight cannot
   *  keep marking the pre-save blueprint (diff-panel). */
  const [mainPickerStepId, setMainPickerStepId] = useState<number | null>(null);
  /** The step the `+` picker is adding to (the legacy `XR Element Auswahl`). */
  /** Where a `+` add lands (ADR-028): a step, or the all-steps set. A discriminated union, not `stepId | null` —
   *  `null` already means "picker closed" (plan-panel Medium). */
  const [addPicker, setAddPicker] = useState<{ kind: "step"; stepId: number } | { kind: "allSteps" } | null>(null);
  /** The file-valued setting the asset library is attaching to (ADR-027 Phase B). */
  const [settingPicker, setSettingPicker] = useState<{ stepId: number | null; elementId: number; name: string; mode: "add" | "replace"; currentFileIds: number[] } | null>(null);
  /** The settings clipboard — session-local, exactly like the legacy's (it never persisted either). */
  const [copiedFrom, setCopiedFrom] = useState<{ elementId: number; label: string; settingNames: string[] } | null>(null);
  const needsAck = canEdit && isPublished && !ackPublished;
  // The EDIT LEASE (ADR-026): acquired on entry, renewed on the server's interval, released on exit/unload. This drives
  // the UI; it is not the security boundary. Server-side enforcement fences ACROSS USERS on every body write — but not
  // yet between two tabs of the SAME user (diff-panel R6-F3), so don't read this as full exclusivity.
  const lock = useManualLock(session, manualId, gate.hasOutstandingWork);
  // Background media work for THIS manual, keyed by step so each card can show its own queued/processing/finished state
  // (ADR-026 Phase 6). One newest job per step — an older finished cut must not mask a newer running one.
  const { jobs, isLoading: jobsLoading, isError: jobsError, retry: retryJobs } = useMediaJobs(session);
  const jobByStep = useMemo(() => {
    const map = new Map<number, (typeof jobs)[number]>();
    for (const job of jobs) {
      if (job.manualId !== manualId || job.stepId == null) continue;
      const seen = map.get(job.stepId);
      if (!seen || job.createdAt > seen.createdAt) map.set(job.stepId, job);
    }
    return map;
  }, [jobs, manualId]);
  /**
   * Which step is the FIRST to use each physical sensor (ADR-037 D7). The `Initalwert` row is offered only there,
   * because a device's starting state is a property of the whole run. Computed once over the loaded body; the legacy
   * asked the same question inside each card's render, rescanning the manual and reloading sensors as it went.
   */
  const initialStateSteps = useMemo(
    () => firstStepSensorMap(body.data?.sections ?? [], "MultiSwitchSensorSetting", body.data?.allStepsElements ?? []),
    [body.data],
  );
  // Editing is frozen until the lease is actually HELD, and again if it is ever lost mid-session. Without the
  // `lockLost` arm the editor would keep autosaving into writes the server now rejects, which is precisely how a
  // conflict turns into lost drafts.
  // ANY lock error ends editing, not only an explicit MANUAL_LOCK_LOST (diff-panel R6-F1): a heartbeat that merely
  // stopped succeeding means the lease is no longer being renewed, which is the same danger with a quieter symptom.
  const lockLost = lock.error !== null;
  // The BANNER needs a stricter test than `readOnly` does (diff-panel R11-F4). `lockLost` fails closed on ANY lock
  // error — including an acquire the server refused because the manual is archived or a link — but "your edit lock
  // ended" would then describe an event that never happened, contradicting the "can't be edited here" notice right
  // above it. `lock.lost` is set only when a lease we actually HELD went away.
  const readOnly = !canEdit || needsAck || refreshing || !lock.held || lockLost; // `refreshing` freezes editing during a conflict-refresh (diff-a1 Critical)
  const sections = useMemo(() => data?.sections ?? [], [data]);
  const exitFacilityId = data?.facilityPath.at(-1)?.id ?? null;

  /**
   * The Unity 3D editor launch (ADR-051) — the ONLY way an author reaches the WebGL path from the rewrite, since
   * the embedded panel is opened by Unity and never by us.
   *
   * The decision is a pure function so it can be tested without standing up the whole editor, and so the control
   * can name WHICH obstacle it hit: "no build deployed here" and "you may not edit this" are different problems
   * and deserve different words.
   */
  // 3D TEST (owner 2026-11-03): the Unity editor as a layer behind the steps, locally and on staging, never in production.
  // Mounted on the first press, then only hidden, as a reload would cost the whole load again.
  const [unityTest, setUnityTest] = useState(false);
  // The address as of the first press: a later change (read-only flips during a refresh) would reload the whole player.
  const [unityTestSrc, setUnityTestSrc] = useState<string | null>(null);
  // Hides the steps over the 3D view, kept mounted: a card may hold unsaved typing.
  const [stepsHidden, setStepsHidden] = useState(false);
  const unityTestBase = unityTestBuildUrl(import.meta.env.DEV, readBootstrap()?.unityEditorUrl, import.meta.env.VITE_UNITY_TEST_URL as string | undefined);
  // ADR-058 D9: on staging Unity's Keycloak is not ours, so the test signs in there by popup and hands Unity that token.
  const unityKeycloak = unityTestKeycloak(import.meta.env.DEV, unityTestBase, session?.realm);
  const unityTestLaunch =
    unityTestBase && exitFacilityId !== null && session?.realm
      ? unityEditorLaunchUrl(
          // Read-only opens Unity read-only too, as the real launch does (ADR-051 D6).
          { facilityId: exitFacilityId, manualId, realm: session.realm, locale: i18n.language, mode: readOnly ? "view" : "edit" },
          unityTestBase,
          unityTestStageUrl(readBootstrap()?.unityEditorUrl, unityKeycloak !== null),
        )
      : null;
  // ADR-058 D1: the embedded start, signed in by our token instead of a redirect.
  const unityTestUrl = unityTestLaunch ? `${unityTestLaunch}&embed=1` : null;
  // Locally there is often no Unity build behind the 3D test (the proxy answers 404): then a still of a scene stands in, so
  // the switch, the glass panel and the scene tools can be tried as they look (owner 2026-11-04). Dev only, never shipped.
  const [unityMissing, setUnityMissing] = useState(false);
  // A failed load of the placeholder (the dev server still busy, say) must not hide it for good: retry a few times.
  const [placeholderTry, setPlaceholderTry] = useState(0);
  useEffect(() => {
    if (!import.meta.env.DEV || !unityTestSrc) return;
    let live = true;
    fetch(new URL(unityTestSrc, window.location.href).pathname, { method: "GET" })
      .then((res) => (res.ok ? res.text() : ""))
      .then((html) => live && setUnityMissing(!/createUnityInstance|UnityLoader|unityInstance/i.test(html)))
      .catch(() => live && setUnityMissing(true));
    return () => {
      live = false;
    };
  }, [unityTestSrc]);
  const unityFrame = useRef<HTMLIFrameElement>(null);
  const [unityError, setUnityError] = useState<EmbedErrorCode | null>(null);
  const [unitySignInFailure, setUnitySignInFailure] = useState<WebglSignInFailure | null>(null);
  const unityTokens = useMemo(() => (unityKeycloak ? createUnityTokenSource({ target: unityKeycloak, onFailure: setUnitySignInFailure }) : null), [unityKeycloak]);
  // A new sign-in after a failed one reloads the frame: Unity has given up by then and waits for a fresh hello.
  const [unityFrameKey, setUnityFrameKey] = useState(0);
  const unityHost = useRef<EmbedHost | null>(null);
  useEffect(() => {
    if (!unityTestSrc) return;
    const host = startEmbedHost({
      frame: () => unityFrame.current,
      unityOrigin: new URL(unityTestSrc).origin,
      getToken: unityTokens ? unityTokens.getToken : getEmbedToken,
      onError: setUnityError,
      onReady: () => setUnityError(null),
    });
    unityHost.current = host;
    // A new frame (a retry after a failed sign-in) has forgotten the focus: the new host sends it once Unity is signed in.
    if (unityFocused.current) host.focusElement(unityFocused.current.elementId, unityFocused.current.rect);
    return () => {
      unityHost.current = null;
      host.dispose();
    };
  }, [unityTestSrc, unityTokens, unityFrameKey]);
  // 3D TEST prototype (design-notes.md): a picked element is selected in Unity and framed in the free area right of the steps.
  const unityFocused = useRef<{ elementId: number; area: string; rect: EmbedRect } | null>(null);
  /** `onlyIfMoved`: a layout change sends again only when the free area really changed, or Unity would fly back and undo the orbit. */
  const focusInUnity = (elementId: number, onlyIfMoved = false) => {
    const frame = unityFrame.current;
    if (!unityTest || !frame) return;
    const view = frame.getBoundingClientRect();
    const column = frame.parentElement?.querySelector("[data-steps-column]");
    const steps = column?.getBoundingClientRect();
    // The free area ends where the steps' pane ends: a docked right column lies over the frame beyond it.
    const pane = column?.parentElement?.getBoundingClientRect();
    const left = Math.max(0, Math.min(view.width, (steps?.right ?? view.left) - view.left));
    const right = Math.max(left, Math.min(view.width, (pane?.right ?? view.right) - view.left));
    const rect = { x: Math.round(left), y: 0, w: Math.round(right - left), h: Math.round(view.height) };
    const area = `${rect.x},${rect.w},${rect.h}`;
    if (onlyIfMoved && unityFocused.current?.area === area) return;
    unityFocused.current = { elementId, area, rect };
    unityHost.current?.focusElement(elementId, rect);
  };
  // `sideOpen` below, declared before it is.
  const rightColumnOpen = assetDrawer != null || mainPickerStepId != null || addPicker != null || settingPicker != null;
  const focusRef = useRef(focusInUnity);
  focusRef.current = focusInUnity;
  // The free area changes with the window and the steps column: send it again, Unity flies anew. The outline and the right
  // column MOVE the column without resizing it, so their toggles restart the observer, whose first report sends again.
  useEffect(() => {
    const frame = unityFrame.current;
    // Off: forget the focus, or switching on again would fly to an element picked long before.
    if (!unityTest) unityFocused.current = null;
    if (!unityTest || !frame) return;
    let timer = 0;
    const observer = new ResizeObserver(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => unityFocused.current != null && focusRef.current(unityFocused.current.elementId, true), 250);
    });
    observer.observe(frame);
    const steps = frame.parentElement?.querySelector("[data-steps-column]");
    if (steps) observer.observe(steps);
    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [unityTest, unityFrameKey, outlineOpen, rightColumnOpen]);



  // On arrival the step that was open before a reload opens again (owner 2026-11-04), else the first one. The ref makes that
  // once only: closing every step afterwards must stay closed. Per tab (sessionStorage): another tab keeps its own place.
  const openedFirst = useRef(false);
  const stepKey = `arc_editor_step_${manualId}`;
  useEffect(() => {
    if (openedFirst.current) return;
    const steps = sections.flatMap((sec) => sec.steps);
    if (steps.length === 0) return;
    openedFirst.current = true;
    let stored: number | null = null;
    try {
      stored = Number(window.sessionStorage.getItem(stepKey)) || null;
    } catch {
      // A blocked storage only costs the place across a reload.
    }
    const again = steps.find((st) => st.id === stored);
    setOpenStepId(again?.id ?? steps[0]!.id);
    if (again) {
      setFocusStepId(again.id);
      setFocusTick((n) => n + 1);
    }
  }, [sections, stepKey]);
  // A manual just made in the create drawer starts with a first section and step, opened (owner 2026-11-04). Only on the
  // drawer's mark — never for an existing empty manual, whose author may want it empty — and once: the mark goes first.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !data || readOnly || !hasNewManualMark(manualId)) return;
    // A single section without a step is a seed a reload cut short: finish it. Anything more is the author's own content.
    const half = sections.length === 1 && sections[0]!.steps.length === 0 ? sections[0] : undefined;
    if (sections.length > 0 && !half) {
      clearNewManualMark(manualId);
      return;
    }
    seeded.current = true;
    void seedFirstStep(gate, m, { sectionName: t("manualEditor.firstSectionName"), stepName: t("manualEditor.firstStepName"), sectionId: half?.id }).then((id) => {
      // Cleared only on success: a failed or interrupted seed is retried the next time the editor opens in this tab.
      if (id === undefined) return;
      clearNewManualMark(manualId);
      setOpenStepId(id);
    });
  }, [data, readOnly, sections.length, manualId, gate, m, t]);
  useEffect(() => {
    if (openStepId == null) return;
    try {
      window.sessionStorage.setItem(stepKey, String(openStepId));
    } catch {
      // See above.
    }
  }, [openStepId, stepKey]);

  // A body/state-stale 409 on any command means someone edited under us — surface a single Refresh banner (rather than
  // silently clobbering) that refetches the authoritative body + clears the errored mutations.
  const allMutations = Object.values(m);
  // A body/state-stale conflict, a permutation race (BODY_ORDER_STALE — the sibling set changed under a reorder/move), OR a
  // late edit that lands after the manual left `draft` (MANUAL_NOT_EDITABLE) / on a linked child (MANUAL_IS_LINK) → surface ONE
  // Refresh banner instead of failing silently (panel Critical + High + editability Medium).
  const errors = allMutations.map((mm) => mm.error);
  const staleError = errors.find(
    (e) =>
      e != null &&
      (e.code === "MANUAL_BODY_STALE" || e.code === "MANUAL_STATE_STALE" || e.code === "BODY_ORDER_STALE" || e.code === "MANUAL_NOT_EDITABLE" || e.code === "MANUAL_IS_LINK"),
  );
  // A rejected structural change that is NOT a "reload" situation (name taken / can't delete the only section) — a dismissible
  // inline message so the outline op doesn't fail silently (panel editability Medium). Cleared by the next successful mutation.
  const conflictError = outlineConflict(errors);
  const [conflictDismissed, setConflictDismissed] = useState(false);
  useEffect(() => setConflictDismissed(false), [conflictError]); // a NEW conflict re-shows the message
  // Explicit conflict-refresh: DISCARD local drafts (their base version is stale — re-saving would just fail) and re-load the
  // authoritative body. Editing is FROZEN (`refreshing`) until the fresh body lands, then the cards remount ON it and the CAS
  // `known` is reseeded — so no draft made on stale content can ride the fresh token and clobber the other author (diff-a1
  // Critical). We deliberately do NOT flush the stale drafts.
  const refreshStale = () => {
    isRefreshing.current = true; // suppress the discarded cards' unmount-flush
    setRefreshing(true); // gate editing during the refetch window
    gate.reseed(); // drop the frozen dirty session + clear the failure latch now
    allMutations.forEach((mm) => mm.reset());
    void body
      .refetch()
      .then((res) => {
        if (res.data) gate.reseed(res.data.manual.version); // adopt the authoritative version AFTER the fresh content loaded
        setReloadKey((k) => k + 1); // remount the cards on the FRESH body (not the stale cache)
      })
      // ALWAYS re-enable editing — a rejected/failed refetch must not leave the whole editor frozen read-only forever (there
      // was no `.catch`, so a transient refetch failure wedged every action off; the stale banner stays to allow a retry).
      .finally(() => setRefreshing(false));
  };

  // AWAIT the pending autosaves before navigating away (nav unmounts the editor and would otherwise tear a final in-flight
  // save down — panel editability High).
  const handleExit = () => {
    // Flush pending autosaves BEFORE leaving; on SUCCESS navigate, on a conflicted save STAY put (the Refresh banner shows)
    // so the edit isn't silently torn down by the unmount (panel #210 High #2).
    void flushAll().then(
      () => {
        if (onExitToFacility && exitFacilityId !== null) onExitToFacility(exitFacilityId);
        else onClose();
      },
      () => {},
    );
  };

  // A VIEW SWITCH (another section, the all-steps set) unmounts the current cards. It used to rely on each card's
  // fire-and-forget unmount flush — whose failure had no card left to retry it and healed to a green exit barrier on
  // the next successful write (gate-panel round-2 Critical: silent loss of the typed text). So, exactly like
  // `handleExit`: AWAIT the barrier first and STAY PUT on a failure (the Refresh banner shows for a conflict; a
  // transient failure keeps the card mounted, where its debounce retries).
  const switchView = (apply: () => void) => {
    void flushAll().then(apply, () => {});
  };
  const closePicks = () => {
    setAssetDrawer((d) => (d?.mode === "manage" ? d : null));
    setMainPickerStepId(null);
    setAddPicker(null);
    setSettingPicker(null);
  };
  const closeSide = () => {
    closePicks();
    setAssetDrawer(null);
  };
  const openSide = (open: () => void) => {
    closeSide();
    open();
  };
  const sideOpen = assetDrawer != null || mainPickerStepId != null || addPicker != null || settingPicker != null;
  const filesTitle = (target?: string) => [t("manualEditor.files"), target].filter(Boolean).join(" · ");
  const stepNameOf = (stepId: number) => sections.flatMap((sec) => sec.steps).find((st) => st.id === stepId)?.name;
  type SettingFileTarget = { stepId: number | null; elementId: number; name: string; mode: "add" | "replace"; currentFileIds: number[] };
  // The steps stay usable beside the column, so what was true when the pick opened may not be any more: read the element when writing.
  const latestBody = useRef(data);
  latestBody.current = data;
  const liveElement = (target: Pick<SettingFileTarget, "stepId" | "elementId">) => {
    const elements =
      target.stepId == null ? latestBody.current?.allStepsElements : latestBody.current?.sections.flatMap((sec) => sec.steps).find((st) => st.id === target.stepId)?.elements;
    return elements?.find((el) => el.id === target.elementId) ?? null;
  };
  const writeSettingFile = (target: SettingFileTarget, fileId: number) => {
    // `add` KEEPS what is already attached — `TrainerVideos` holds one video per trainer, and a single-id write replaces the
    // whole list (diff-panel R1 High #1a).
    const fileIdsNow = () => {
      if (target.mode !== "add") return [fileId];
      const attached = liveElement(target)?.settings.find((x) => x.name === target.name)?.files.map((f) => f.fileId);
      return [...new Set([...(attached ?? target.currentFileIds), fileId])];
    };
    void flushAll()
      .catch(() => undefined)
      .then(() => {
        if (liveElement(target) == null) return undefined; // removed beside the open column
        // `stepId: null` = the all-steps set (ADR-028) → the manual-level twin.
        return target.stepId != null
          ? gate.enqueue(m.setElementSetting, (expectedVersion) => ({ stepId: target.stepId!, elementId: target.elementId, name: target.name, fileIds: fileIdsNow(), expectedVersion }))
          : gate.enqueue(m.setManualElementSetting, (expectedVersion) => ({ elementId: target.elementId, name: target.name, fileIds: fileIdsNow(), expectedVersion }));
      })
      .catch(() => undefined);
  };
  const pickSettingFile = (target: SettingFileTarget, dropped?: DroppedFile) => {
    if (!dropped) openSide(() => setSettingPicker(target));
    else if (target.mode === "replace" && target.currentFileIds.length > 0) setOverwrite({ name: dropped.name, run: () => writeSettingFile(target, dropped.fileId) });
    else writeSettingFile(target, dropped.fileId);
  };
  // Dropped from the right column. Replacing something already there asks first (owner 2026-11-02): a drop is easy to miss by a row.
  const dropOnStep = (step: ManualBodySection["steps"][number], drag: SideDrag) => {
    const stepId = step.id;
    const then = (write: () => Promise<unknown>) =>
      void flushAll()
        .catch(() => undefined)
        .then(write)
        .catch(() => undefined);
    if (drag.kind === "element") {
      then(() => gate.enqueue(m.addStepElement, (expectedVersion) => ({ stepId, blueprintId: drag.blueprintId, sensorId: drag.sensorId, expectedVersion })));
      return;
    }
    const attach = () => then(() => gate.enqueue(m.attachStepMedia, (expectedVersion) => ({ expectedVersion, stepId, fileId: drag.fileId })));
    if (step.media) setOverwrite({ name: drag.name, run: attach });
    else attach();
  };
  const selectStep = (stepId: number) => {
    switchView(() => {
      closePicks();
      setViewAllSteps(false);
      setOpenStepId(stepId);
      setFocusStepId(stepId);
      setFocusTick((n) => n + 1);
    });
  };
  const closeStep = () =>
    switchView(() => {
      closePicks();
      setOpenStepId(null);
    });
  const allStepsEntry = () =>
    switchView(() => {
      closePicks();
      setViewAllSteps(true);
      // Drop the open step: the outline otherwise keeps it `aria-current` beside the All-steps entry (diff-panel consensus).
      setOpenStepId(null);
      setFocusStepId(null);
    });

  // Someone else is editing → refuse ENTRY outright rather than opening a read-only shell the author will try to
  // type into. Legacy parity: the old UI showed this dialog and aborted (`ARLMS_ManualView.js` DisplayManual).
  //
  // Placed AFTER every hook, deliberately: an earlier return here crashed the editor with React's "Rendered fewer
  // hooks than expected" — the blocked path ran a shorter hook list than the normal one.
  if (lock.blockedBy) {
    return (
      <ManualLockedNotice
        holderName={lock.blockedBy.name}
        waiting={lock.state?.waiting ?? null}
        onBack={() => (onExitToFacility && exitFacilityId !== null ? onExitToFacility(exitFacilityId) : onClose())}
      />
    );
  }

  return (
    <ModalOverlay isDismissable isKeyboardDismissDisabled={stepDragActive || sideOpen} isOpen onOpenChange={(open) => !open && handleExit()} className="bg-background fixed inset-0 z-50">
      <Modal className="h-full w-full outline-hidden">
        <Dialog aria-label={data ? t("manualEditor.editingName", { name: data.manual.name }) : t("manualEditor.manualEditor")} className="flex h-full flex-col outline-hidden">
          <header className="border-border bg-background flex flex-wrap items-center gap-2 border-b px-4 py-2.5 max-sm:gap-1.5 max-sm:px-3 max-sm:py-2">
            <Button variant="outline" size="sm" onPress={handleExit} className="dark:border-border shrink-0">
              <ChevronLeft aria-hidden="true" />
              {t("facilitySelection.nav.back")}
            </Button>
            <h1 className="ml-1 min-w-0 truncate text-lg font-semibold">{data?.manual.name ?? "…"}</h1>
            {data?.facilityPath.at(-1) ? <span className="text-muted-foreground hidden min-w-0 truncate text-sm md:inline">{data.facilityPath.at(-1)?.name}</span> : null}
            <div className="ml-auto flex flex-wrap items-center justify-end gap-2 max-sm:w-full max-sm:flex-nowrap max-sm:gap-1.5">
              <SearchField
                label={t("manualEditor.searchSectionsAndSteps")}
                placeholder={t("manualEditor.searchShort")}
                value={query}
                onChange={setQuery}
                clearLabel={t("manualEditor.clearSearch")}
                className="w-44 max-sm:w-auto max-sm:min-w-0 max-sm:flex-1"
              />
              {/* The text goes first when the width runs out, the icons stay; the 3D editor keeps its name longest. */}
              <TooltipTrigger delay={300} focusable={false}>
                <Button variant={assetDrawer?.mode === "manage" ? "secondary" : "outline"} size="sm" aria-label={t("manualEditor.files")} isDisabled={!data} aria-pressed={assetDrawer?.mode === "manage"} onPress={() => (assetDrawer?.mode === "manage" ? closeSide() : openSide(() => setAssetDrawer({ mode: "manage", stepId: null })))} className="dark:border-border gap-1.5">
                  <Folder aria-hidden="true" />
                  <span className="max-xl:hidden">{t("manualEditor.files")}</span>
                </Button>
                <Tooltip>{t("manualEditor.files")}</Tooltip>
              </TooltipTrigger>
              <TooltipTrigger delay={300} focusable={false}>
                <Button variant="outline" size="sm" aria-label={t("manualEditor.preview")} isDisabled={!data} onPress={() => setPreviewOpen(true)} className="dark:border-border gap-1.5">
                  <Eye aria-hidden="true" />
                  <span className="max-xl:hidden">{t("manualEditor.preview")}</span>
                </Button>
                <Tooltip>{t("manualEditor.preview")}</Tooltip>
              </TooltipTrigger>
              {/* 3D TEST view switch: a segmented control, as a view is a mode of this page, not an action (design pass 2026-11-04).
                  Not on a 2D-only manual, which has no scene (design-notes "Availability"). */}
              {unityTestUrl && isArManual ? (
                <div role="group" aria-label={t("manualEditor.viewMode")} className="bg-muted flex rounded-lg p-0.5 max-lg:hidden">
                  {([false, true] as const).map((on) => (
                    <Button
                      key={String(on)}
                      variant="ghost"
                      size="sm"
                      aria-pressed={unityTest === on}
                      onPress={() => {
                        if (unityTest === on) return;
                        // In the click itself: the popup needs the user activation. A running or held session makes it a no-op.
                        if (on) unityTokens?.signIn();
                        else setStepsHidden(false); // back in 2D the steps must show: the scene that replaced them is gone
                        setUnityTestSrc((src) => src ?? unityTestUrl);
                        setUnityTest(on);
                      }}
                      className={cn("gap-1.5 rounded-md transition-[background-color,box-shadow] duration-200", unityTest === on ? "bg-background text-foreground hover:bg-background ring-border/50 font-semibold shadow-xs ring-1 dark:bg-card dark:hover:bg-card" : "text-muted-foreground hover:text-foreground")}
                    >
                      {on ? <Box aria-hidden="true" /> : <SlidersHorizontal aria-hidden="true" />}
                      {on ? t("manualEditor.view3d") : t("manualEditor.view2d")}
                    </Button>
                  ))}
                </div>
              ) : null}
              {data ? (
                <EditorStatusControl
                  session={session}
                  manual={{ id: data.manual.id, state: data.manual.state, markedComment: data.manual.markedComment }}
                  canManage={data.capabilities.canReport}
                  canConfirm={canConfirm}
                  onBeforeAction={flushAll}
                  body={data}
                />
              ) : null}
              <span className="bg-border mx-1 h-6 w-px max-sm:hidden" aria-hidden="true" />
              {/* Not on a phone: one toolbar row there, and both live in the app's own header too. */}
              <span className="contents max-sm:hidden">
                <BackgroundJobs jobs={jobs} isLoading={jobsLoading} isError={jobsError} onRetry={retryJobs} />
                <ThemeToggle />
              </span>
              <TooltipTrigger delay={300} focusable={false}>
                <Button variant="ghost" size="icon" aria-label={t("manualEditor.settings")} isDisabled={!data || readOnly || data.manual.parentManualId !== null} onPress={() => setSettingsOpen(true)}>
                  <Settings aria-hidden="true" className="size-4" />
                </Button>
                <Tooltip>{t("manualEditor.settings")}</Tooltip>
              </TooltipTrigger>
            </div>
          </header>

          {data && !canEdit ? (
            <div className="border-border bg-muted/40 text-muted-foreground border-b px-5 py-2 text-sm">
              {data.manual.parentManualId != null
                ? t("manualEditor.thisIsALinkedCopyEdit")
                : t("manualEditor.thisManualCantBeEditedHere")}
            </div>
          ) : null}
          {lock.lost ? (
            <div role="alert" className="border-border bg-attention/10 text-attention flex items-center justify-between gap-3 border-b px-5 py-2 text-sm">
              {/* The lease lapsed (laptop sleep / lost network) or was handed over. Editing is already frozen via
                  `readOnly`; saying so is what stops the author typing into writes the server will now reject. */}
              <span>{t("manualEditor.lockEnded")}</span>
            </div>
          ) : null}
          {staleError ? (
            <div className="border-border bg-destructive/10 text-destructive flex items-center justify-between gap-3 border-b px-5 py-2 text-sm">
              <span>{t("manualEditor.staleReload")}</span>
              <Button variant="outline" size="sm" onPress={refreshStale} className="dark:border-border gap-1.5">
                <RefreshCw aria-hidden="true" className="size-3.5" />
                {t("manualEditor.refresh")}
              </Button>
            </div>
          ) : null}
          {conflictError && !conflictDismissed ? (
            <div className="border-border bg-destructive/10 text-destructive flex items-center justify-between gap-3 border-b px-5 py-2 text-sm">
              <span>
                {t(conflictError.messageKey)}
              </span>
              <Button variant="ghost" size="sm" onPress={() => setConflictDismissed(true)}>
                {t("manualEditor.dismiss")}
              </Button>
            </div>
          ) : null}

          <div
            className="relative flex min-h-0 flex-1 overflow-hidden"
            onKeyDown={(e) => {
              if (e.key === "Escape" && sideOpen) closeSide();
            }}
          >
            {unityTestSrc && unityMissing ? (
              <div className={cn("bg-muted absolute inset-0 z-0", !unityTest && "invisible")}>
                <img
                  key={placeholderTry}
                  src={placeholderTry === 0 ? "/__dev3d/webscreenshot2.png" : `/__dev3d/webscreenshot2.png?retry=${placeholderTry}`}
                  alt=""
                  className="size-full object-cover"
                  onError={() => placeholderTry < 5 && window.setTimeout(() => setPlaceholderTry((n) => n + 1), 1500)}
                />
                <p className="bg-background/85 text-muted-foreground absolute right-3 bottom-3 m-0 rounded-full px-3 py-1 text-xs shadow-sm backdrop-blur-sm">
                  {t("manualEditor.unityPlaceholder")}
                </p>
              </div>
            ) : unityTestSrc ? (
              <iframe
                key={unityFrameKey}
                ref={unityFrame}
                title={t("manualEditor.unityTest")}
                src={unityTestSrc}
                className={cn("absolute inset-0 z-0 h-full w-full border-0", !unityTest && "invisible")}
              />
            ) : null}
            {/* The scene's own tools, only with the scene on: a floating glass bar on it (in 2D they hung in the page, attached
                to nothing). Not while the right column is open: it would sit on the column's close button. */}
            {unityTestUrl && unityTest && !sideOpen ? (
              <div className="animate-in fade-in-0 slide-in-from-top-2 absolute top-3 right-4 z-30 flex flex-wrap items-center justify-end gap-2 duration-300 max-lg:hidden motion-reduce:animate-none">
                {unityTest && !unityMissing && unityTokens && (unitySignInFailure || unityError === "timeout" || unityError === "sessionEnded") ? (
                  <>
                    <p role="alert" className="bg-destructive/10 text-destructive m-0 rounded-md px-2 py-1 text-xs">
                      {unitySignInFailure ? t("manualEditor.unitySignInError") : t(`manualEditor.unityEmbedError.${unityError!}`)}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onPress={() => {
                        setUnitySignInFailure(null);
                        setUnityError(null);
                        // Unity has stopped (no token in its 30 s, or the session ended): sign in if needed, then start it again.
                        unityTokens.signIn();
                        setUnityFrameKey((k) => k + 1);
                      }}
                      className="dark:border-border shadow-sm"
                    >
                      <RefreshCw aria-hidden="true" />
                      {t("manualEditor.unitySignInRetry")}
                    </Button>
                  </>
                ) : unityError && unityTest && !unityMissing ? (
                  <p role="alert" className="bg-destructive/10 text-destructive m-0 rounded-md px-2 py-1 text-xs">
                    {t(`manualEditor.unityEmbedError.${unityError}`)}
                  </p>
                ) : null}
              </div>
            ) : null}
            {/* Far left, full height (owner 2026-11-02): dots, unfolded the outline. It pushes the steps aside where there is room and
                lies over them below `lg`, where the steps would otherwise run out of width. */}
            {data ? (
              <div
                className={cn(
                  // Not on a phone: the list of steps is the outline there, and the rail took an eighth of the width.
                  "border-border bg-background relative z-10 shrink-0 overflow-hidden border-r max-sm:hidden",
                  outlineOpen ? "absolute inset-y-0 left-0 z-20 w-72 shadow-xl lg:relative lg:shadow-none" : "w-11",
                )}
              >
                {outlineOpen ? (
                  <OutlineEditorPanel
                    sections={sections}
                    currentStepId={viewAllSteps ? null : openStepId}
                    gate={gate}
                    disabled={readOnly}
                    m={m}
                    query={query}
                    onCollapse={() => setOutlineOpen(false)}
                    onSelectStep={selectStep}
                    onDragActiveChange={setStepDragActive}
                    allSteps={isArManual ? { count: data.allStepsElements.length, selected: viewAllSteps, onSelect: allStepsEntry } : null}
                  />
                ) : (
                  <StepDots
                    sections={sections}
                    currentStepId={viewAllSteps ? null : openStepId}
                    onSelectStep={selectStep}
                    onExpand={() => setOutlineOpen(true)}
                    allSteps={isArManual ? { selected: viewAllSteps, onSelect: allStepsEntry } : null}
                  />
                )}
              </div>
            ) : null}
            {/* A spacer keeps the steps from sitting under the folded outline's overlay below `lg`. */}
            {outlineOpen && data ? <div aria-hidden="true" className="w-11 shrink-0 max-sm:hidden lg:hidden" /> : null}

            <ElementInfoProvider session={session} manualId={manualId} catalogueAvailable={isArManual && data?.manual.parentManualId == null}>
            <div
              className={cn(
                "scroll-thin relative z-10 min-w-0 flex-1 overflow-auto",
                stepsHidden && "[&>:not([data-steps-pill])]:invisible",
                // Over the 3D view: the gaps pass the pointer to Unity, the steps and headings keep it.
                unityTest
                  ? "pointer-events-none bg-transparent [&_:is(button,input,textarea,[data-section-head],[data-step-id])]:pointer-events-auto"
                  : "bg-muted",
              )}
            >
              {/* Steps hidden to position in the scene: the way back sits where the panel was, not in a far corner (owner
                  2026-11-04: "rechts oben sieht ihn keiner"). Sticky + zero height, so it neither scrolls away nor pushes. */}
              {unityTest && stepsHidden ? (
                <div data-steps-pill className="sticky top-0 z-20 h-0">
                  <Button variant="outline" size="sm" aria-pressed onPress={() => setStepsHidden(false)} className="animate-in fade-in-0 slide-in-from-left-2 bg-background/85 m-3 gap-1.5 rounded-full border-white/50 shadow-lg backdrop-blur-xl duration-200 motion-reduce:animate-none dark:border-white/10 dark:bg-background/75">
                    <SlidersHorizontal aria-hidden="true" />
                    {t("manualEditor.showSteps")}
                  </Button>
                </div>
              ) : null}
              {body.isPending ? (
                <p className="text-muted-foreground flex items-center gap-2 p-6 text-sm">
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" /> {t("common.loading")}
                </p>
              ) : body.isError || !data ? (
                <p className="text-destructive p-6 text-sm">{t("manualEditor.loadEditFailed")}</p>
              ) : viewAllSteps && isArManual ? (
                <div className="mx-auto flex max-w-5xl flex-col gap-5 p-6">
                  <AllStepsCard
                    elements={data.allStepsElements}
                    initialStateSensorIds={initialStateSteps.get(ALL_STEPS_KEY)}
                    disabled={readOnly}
                    m={m}
                    onAddElement={readOnly ? undefined : () => openSide(() => setAddPicker({ kind: "allSteps" }))}
                    onRemoveElement={
                      readOnly
                        ? undefined
                        : (elementId) => {
                            void flushAll()
                              .catch(() => undefined)
                              .then(() => gate.enqueue(m.removeManualElement, (expectedVersion) => ({ elementId, expectedVersion })))
                              .catch(() => undefined);
                          }
                    }
                    onDuplicateElement={
                      readOnly
                        ? undefined
                        : (elementId) =>
                            flushAll()
                              .catch(() => undefined)
                              .then(() => gate.enqueue(m.duplicateManualElement, (expectedVersion) => ({ elementId, expectedVersion })))
                              .catch(() => undefined)
                    }
                    onSetSetting={
                      readOnly
                        ? undefined
                        : (elementId, name, patch) => {
                            void flushAll()
                              .catch(() => undefined)
                              .then(() => gate.enqueue(m.setManualElementSetting, (expectedVersion) => ({ elementId, name, ...patch, expectedVersion })))
                              .catch(() => undefined);
                          }
                    }
                    onPickSettingFile={readOnly ? undefined : (elementId, name, mode, currentFileIds, dropped) => pickSettingFile({ stepId: null, elementId, name, mode, currentFileIds }, dropped)}
                    copiedFrom={copiedFrom}
                    onCopySettings={readOnly ? undefined : (elementId, label, settingNames) => setCopiedFrom({ elementId, label, settingNames })}
                    onPasteSettings={
                      readOnly
                        ? undefined
                        : (elementId) => {
                            const source = copiedFrom;
                            if (source == null) return;
                            void flushAll()
                              .catch(() => undefined)
                              .then(() => gate.enqueue(m.copyManualElementSettings, (expectedVersion) => ({ elementId, sourceElementId: source.elementId, expectedVersion })))
                              .catch(() => undefined);
                          }
                    }
                  />
                </div>
              ) : (
                <SectionPane
                  session={session}
                  sections={sections}
                  query={query}
                  openStepId={openStepId}
                  onOpenStep={selectStep}
                  onCloseStep={closeStep}
                  allOpen={allStepsOpen}
                  onActivateStep={setOpenStepId}
                  gate={gate}
                  readOnly={readOnly}
                  m={m}
                  manualId={manualId}
                  onPickMain={(stepId) => openSide(() => setMainPickerStepId(stepId))}
                  onAddElement={(stepId) => openSide(() => setAddPicker({ kind: "step", stepId }))}
                  onSetSetting={(stepId, elementId, name, patch) => {
                    // Same flush-first rule as every other structural write: the gate adopts the version ITS write
                    // returns, so an unflushed text buffer could later save against a version it was not authored on.
                    void flushAll()
                      .catch(() => undefined)
                      .then(() => gate.enqueue(m.setElementSetting, (expectedVersion) => ({ stepId, elementId, name, ...patch, expectedVersion })))
                      .catch(() => undefined);
                  }}
                  onPickSettingFile={(stepId, elementId, name, mode, currentFileIds, dropped) => pickSettingFile({ stepId, elementId, name, mode, currentFileIds }, dropped)}
                  copiedFrom={copiedFrom}
                  onCopySettings={(elementId, label, settingNames) => setCopiedFrom({ elementId, label, settingNames })}
                  onPasteSettings={(stepId, elementId) => {
                    const source = copiedFrom;
                    if (source == null) return;
                    void flushAll()
                      .catch(() => undefined)
                      .then(() => gate.enqueue(m.copyElementSettings, (expectedVersion) => ({ stepId, elementId, sourceElementId: source.elementId, expectedVersion })))
                      .catch(() => undefined);
                  }}
                  onRemoveElement={(stepId, elementId) => {
                    void flushAll()
                      .catch(() => undefined)
                      .then(() => gate.enqueue(m.removeStepElement, (expectedVersion) => ({ stepId, elementId, expectedVersion })))
                      .catch(() => undefined);
                  }}
                  onDuplicateElement={(stepId, elementId) =>
                    flushAll()
                      .catch(() => undefined)
                      .then(() => gate.enqueue(m.duplicateStepElement, (expectedVersion) => ({ stepId, elementId, expectedVersion })))
                      .catch(() => undefined)
                  }
                  isArManual={isArManual}
                  onDropOnStep={dropOnStep}
                  focusStepId={focusStepId}
                  focusTick={focusTick}
                  jobByStep={jobByStep}
                  initialStateSteps={initialStateSteps}
                  reloadKey={reloadKey}
                  registerFlush={registerFlush}
                  unregisterFlush={unregisterFlush}
                  isRefreshing={isRefreshing}
                  onPickMedia={readOnly ? undefined : (stepId) => openSide(() => setAssetDrawer({ mode: "pick", stepId }))}
                  onDetachMedia={
                    readOnly
                      ? undefined
                      : (stepId) => {
                          void flushAll()
                            .catch(() => undefined)
                            .then(() => gate.enqueue(m.detachStepMedia, (expectedVersion) => ({ stepId, expectedVersion })))
                            .catch(() => undefined);
                        }
                  }
                  onElementSelected={focusInUnity}
                  besideUnity={unityTest}
                  onHideSteps={() => setStepsHidden(true)}
                />
              )}
            </div>
            </ElementInfoProvider>
          {/* The right column (owner 2026-11-02): only on demand, opened by a click, one thing at a time. Files come from the
                header button or a pick, elements only from a plus. The steps stay usable beside it. */}
          {assetDrawer ? (
            <AssetLibraryDrawer
              docked
              // A pick names what it goes to: the steps stay usable beside it.
              title={filesTitle(assetDrawer.stepId != null ? stepNameOf(assetDrawer.stepId) : undefined)}
              hint={t("manualEditor.dragFilesHint")}
              session={session}
              isOpen
              mode={assetDrawer.mode}
              manualId={manualId}
              types={assetDrawer.mode === "pick" ? MEDIA_TYPES : null}
              onClose={() => setAssetDrawer(null)}
              onSelect={(file: AssetFile) => {
                // Attach the picked library file to the target step (ADR-024 11a-4) THROUGH the gate (serialized + shared CAS
                // version). The mutation seeds the returned body into cache so the step card's thumbnail updates live; a stale
                // 409 surfaces via the Refresh banner (allMutations).
                const stepId = assetDrawer.stepId;
                if (stepId != null) void gate.enqueue(m.attachStepMedia, (expectedVersion) => ({ expectedVersion, stepId, fileId: file.fileId })).catch(() => {});
                setAssetDrawer(null);
              }}
            />
          ) : null}

          {mainPickerStepId != null ? (
            <BlueprintPickerDrawer
              docked
              target={stepNameOf(mainPickerStepId)}
              session={session}
              manualId={manualId}
              mode="main"
              isSensorManual={data?.manual.isSensorManual ?? false}
              // Derived from the LIVE body every render, never snapshotted when the drawer opened.
              currentBlueprintId={
                data?.sections.flatMap((sec) => sec.steps).find((st) => st.id === mainPickerStepId)?.elements.find((e) => e.isMain)?.blueprintId ?? null
              }
              isSaving={m.setStepMainElement.isPending}
              onClose={() => setMainPickerStepId(null)}
              onSelect={(blueprintId) => {
                const stepId = mainPickerStepId;
                setMainPickerStepId(null);
                // FLUSH pending text BEFORE enqueuing (diff-panel High). The gate adopts the version its own write
                // returns, so a write that skipped the flush could hand the gate a version fresher than the buffered
                // text was authored against — and that text would then save over whoever bumped it, with no 409.
                void flushAll()
                  .catch(() => undefined)
                  .then(() => gate.enqueue(m.setStepMainElement, (expectedVersion) => ({ stepId, blueprintId, expectedVersion })))
                  .catch(() => undefined);
              }}
            />
          ) : null}

          {addPicker != null ? (
            <BlueprintPickerDrawer
              docked
              target={addPicker.kind === "step" ? stepNameOf(addPicker.stepId) : t("manualEditor.allSteps")}
              session={session}
              manualId={manualId}
              mode="add"
              isSensorManual={data?.manual.isSensorManual ?? false}
              // A sensor element belongs to a STEP, never to "Alle Schritte" (ADR-038 D10).
              allowSensorCategory={addPicker.kind === "step"}
              canUseNonPlaceable={data?.capabilities.canUseNonPlaceableElements ?? false}
              isSaving={m.addStepElement.isPending || m.addManualElement.isPending}
              onClose={() => setAddPicker(null)}
              onSelect={(blueprintId, sensorId) => {
                const target = addPicker;
                setAddPicker(null);
                void flushAll()
                  .catch(() => undefined)
                  .then(() =>
                    target.kind === "step"
                      ? // ADR-038: a sensor tile names its DEVICE too. The server requires it for a sensor blueprint
                        // and refuses it for anything else, so it is passed through rather than guessed at here.
                        gate.enqueue(m.addStepElement, (expectedVersion) => ({ stepId: target.stepId, blueprintId, sensorId, expectedVersion }))
                      : gate.enqueue(m.addManualElement, (expectedVersion) => ({ blueprintId, expectedVersion })),
                  )
                  .catch(() => undefined);
              }}
            />
          ) : null}

          {settingPicker ? (
            <AssetLibraryDrawer
              docked
              title={filesTitle(liveElement(settingPicker)?.displayName?.trim() || liveElement(settingPicker)?.name)}
              session={session}
              manualId={manualId}
              isOpen
              mode="pick"
              // EVERY file-valued name gets its own filter, not just `MeshObject`: offering an image for a `Video`
              // setting (or a model for `Image`) attaches something Unity cannot load, and it fails silently in AR.
              // The server enforces the same lists — this only keeps the author from being offered the wrong thing.
              types={SETTING_FILE_TYPES[settingPicker.name] ?? null}
              onClose={() => setSettingPicker(null)}
              onSelect={(file) => {
                const target = settingPicker;
                setSettingPicker(null);
                writeSettingFile(target, file.fileId);
              }}
            />
          ) : null}
          </div>


          {/* Preview (Eye) — the shipped read-only reader over the SAME id, as a nested overlay (verify-my-edits loop). */}
          {settingsOpen && data ? (
            <ManualSettingsDialog manual={data.manual} session={session} isSuper={me.data?.isSuper ?? false} onClose={() => setSettingsOpen(false)} onSaved={() => undefined} />
          ) : null}
          {previewOpen && data ? (
            <ManualViewer preview key={`preview-${manualId}`} session={session} manualId={manualId} onClose={() => setPreviewOpen(false)} />
          ) : null}

          <ConfirmDialog
            isOpen={overwrite != null}
            title={t("manualEditor.replaceMedia")}
            message={t("manualEditor.replaceFileBody", { name: overwrite?.name ?? "" })}
            confirmLabel={t("manualEditor.replace")}
            onCancel={() => setOverwrite(null)}
            onConfirm={() => {
              overwrite?.run();
              setOverwrite(null);
            }}
          />

          {/* Published-edit warning (the legacy `editManualConfirmation`) — a published manual IS editable (ADR-018 amendment),
              but edits apply live, so we confirm intent once before unlocking. Cancel / Esc / backdrop exits the editor. */}
          {/* The published-manual gate. NOT a nested `ModalOverlay` any more (owner bug report 2026-09-02): a second
              react-aria Modal portals OUTSIDE this overlay, so clicking t("manualEditor.editAnyway") read as an outside click here and
              ran `handleExit()` — you were bounced to the facility manuals view and needed a refresh to get back in.
              It was also `aria-hidden` to screen readers. See `PublishedManualGate`. */}
          {needsAck && data ? (
            <PublishedManualGate
              manualName={data.manual.name}
              state={data.manual.state === "marked" ? "marked" : "confirmed"}
              onCancel={handleExit}
              onContinue={() => setAckPublished(true)}
            />
          ) : null}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

type Mutations = ReturnType<typeof useManualBodyMutations>;

/** Takes what is dragged out of the right column: an element where a step can hold one, an image or video where the step shows media. */
function StepDropZone({
  step,
  readOnly,
  isArManual,
  onDrop,
  hidden = false,
  children,
}: {
  step: ManualBodySection["steps"][number];
  readOnly: boolean;
  isArManual: boolean;
  onDrop: (step: ManualBodySection["steps"][number], drag: SideDrag) => void;
  /** Kept mounted but not shown (a search with every step open). */
  hidden?: boolean;
  children: React.ReactNode;
}) {
  const { isOver, dropProps } = useSideDrop(
    (drag) => !readOnly && (drag.kind === "element" ? isArManual : fileMatches(drag.name, MEDIA_TYPES) && sectionsForStep(step).includes("standard")),
    (drag) => onDrop(step, drag),
  );
  return (
    <div {...dropProps} data-step-id={step.id} className={cn("relative scroll-mt-3 rounded-xl", isOver && "ring-primary ring-offset-background ring-2 ring-offset-2", hidden && "hidden")}>
      {children}
    </div>
  );
}

function mainNameOf(step: ManualBodySection["steps"][number]): string | null {
  const main = step.elements.find((el) => el.isMain);
  return main?.displayName?.trim() || main?.name || null;
}

/** A folded step: what it is, what it says, and whether something is missing. */
function StepRow({ step, onOpen, menu }: { step: ManualBodySection["steps"][number]; onOpen: () => void; menu: React.ReactNode }) {
  const { t } = useTranslation();
  const Icon = STEP_TYPE_ICON[step.type];
  // No text excerpt: a folded step says its name, its main element and how many elements it holds — the rest is in the
  // card (owner 2026-11-04: "zu viel Text bei den Schritten"; the main element on every row, then ", n Elemente").
  const mainName = mainNameOf(step);
  const extras = step.elements.filter((el) => !el.isMain).length;
  const unplaced = step.elements.filter((el) => !el.isMain && !el.hasBeenPositioned).length;
  const summary = [mainName, extras > 0 ? t("manualEditor.countElements", { count: extras }) : null].filter(Boolean).join(", ");
  return (
    <div className="border-border/70 bg-background hover:border-ring/50 flex items-center gap-1 rounded-xl border pr-1.5 shadow-xs transition-[border-color,box-shadow,transform] duration-150 ease-out hover:-translate-y-px hover:shadow-sm motion-reduce:transition-none motion-reduce:hover:translate-y-0">
      <button type="button" aria-expanded={false} onClick={onOpen} className="focus-visible:ring-primary flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-xl border-0 bg-transparent px-4 py-2.5 text-left outline-none focus-visible:ring-2">
        <Icon aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
        <span className="shrink-0 font-medium">{step.name}</span>
        {/* Beside the name: the main element, then ", n Elemente" (owner 2026-11-04 — no badge, plain text). */}
        {summary ? <span className="text-muted-foreground min-w-0 truncate text-sm">{summary}</span> : null}
      </button>
      {unplaced > 0 ? <span className="text-attention mx-1 shrink-0 text-xs whitespace-nowrap">{unplaced} {t("manualEditor.notPlaced")}</span> : null}
      {menu}
    </div>
  );
}

/**
 * The main pane: every section with its steps, one step open as a card and the others as rows (owner 2026-11-02). Only the open
 * step is mounted, so switching goes through the editor's flush barrier first, or its unsaved text would be lost with the card.
 */
function SectionPane({
  session,
  sections,
  query,
  openStepId,
  onOpenStep,
  onCloseStep,
  allOpen,
  onActivateStep,
  gate,
  readOnly,
  m,
  manualId,
  onPickMain,
  onAddElement,
  onRemoveElement,
  onDuplicateElement,
  onSetSetting,
  onPickSettingFile,
  onDropOnStep,
  copiedFrom,
  onCopySettings,
  onPasteSettings,
  isArManual,
  focusStepId,
  focusTick,
  jobByStep,
  initialStateSteps,
  reloadKey,
  registerFlush,
  unregisterFlush,
  isRefreshing,
  onPickMedia,
  onDetachMedia,
  onElementSelected,
  besideUnity = false,
  onHideSteps,
}: {
  session: AuthSession | null;
  sections: ManualBodySection[];
  query: string;
  openStepId: number | null;
  onOpenStep: (stepId: number) => void;
  onCloseStep: () => void;
  /** Every step is a card (a 2D-only manual): the dots and the outline jump, and the current step follows the scrolling. */
  allOpen: boolean;
  /** Makes a step the current one without the flush barrier: with every card mounted, nothing unmounts. */
  onActivateStep: (stepId: number) => void;
  gate: BodyWriteGate;
  readOnly: boolean;
  m: Mutations;
  /** The manual in context (#217 P2) — threaded on to each StepCard's collage picker for the "This manual" asset scope. */
  manualId: number | null;
  onPickMain: (stepId: number) => void;
  onAddElement: (stepId: number) => void;
  onRemoveElement: (stepId: number, elementId: number) => void;
  onDuplicateElement: (stepId: number, elementId: number) => Promise<ManualBody | void>;
  onSetSetting: (stepId: number, elementId: number, name: string, patch: { value?: string | null; fileIds?: number[] }) => void;
  onPickSettingFile: (stepId: number, elementId: number, name: string, mode: "add" | "replace", currentFileIds: number[], dropped?: DroppedFile) => void;
  onDropOnStep: (step: ManualBodySection["steps"][number], drag: SideDrag) => void;
  copiedFrom: { elementId: number; label: string; settingNames: string[] } | null;
  onCopySettings: (elementId: number, label: string, settingNames: string[]) => void;
  onPasteSettings: (stepId: number, elementId: number) => void;
  isArManual: boolean;
  focusStepId: number | null;
  focusTick: number;
  jobByStep: Map<number, import("@arc/contracts").MediaJob>;
  /** Step id → the sensors that step is the FIRST in the manual to use (ADR-037 D7). */
  initialStateSteps: Map<number, Set<number>>;
  /** Bumped on a conflict-refresh to force-remount the cards (drop stale local drafts). */
  reloadKey: number;
  registerFlush: (fn: () => Promise<void>) => void;
  unregisterFlush: (fn: () => Promise<void>) => void;
  isRefreshing: { current: boolean };
  /** Open the asset library (pick mode) for a step; undefined when read-only. */
  onPickMedia?: (stepId: number) => void;
  onDetachMedia?: (stepId: number) => void;
  /** An element picked in a step card (the 3D test frames it in Unity). */
  onElementSelected?: (elementId: number) => void;
  /** The 3D test is on: the steps narrow and move to the left, leaving the right for the view (design-notes.md). */
  besideUnity?: boolean;
  /** Over the 3D view: hide the steps to position elements in the scene. */
  onHideSteps?: () => void;
}) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  /** An insert slot being named: in front of that step, or at the end of that section. */
  const [addingStep, setAddingStep] = useState<{ sectionId: number; beforeId?: number } | null>(null);
  const [addingSection, setAddingSection] = useState<{ beforeId?: number } | null>(null);
  const [renamingSection, setRenamingSection] = useState<number | null>(null);
  // "Umbenennen" on a folded step: its name field only exists once the card is open.
  const [renameOnOpen, setRenameOnOpen] = useState<number | null>(null);

  // Bring the just-opened step into view. NOT on every body change: `sections` is new after each save, which would pull the
  // author back to this step while they work further down.
  const jumpedAt = useRef(0);
  useEffect(() => {
    if (focusStepId == null || !scrollRef.current) return;
    // With every step open a jump lands at the step's head; one open card only needs to come into view. The scroll that
    // follows must not move the current step on: near the end the step's head cannot reach the top.
    jumpedAt.current = Date.now();
    const target = () => scrollRef.current?.querySelector(`[data-step-id="${focusStepId}"]`);
    if (!allOpen) {
      target()?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      return;
    }
    // Cards mount as the jump passes them and grow, which moves the target: jump at once, then align again once they have.
    target()?.scrollIntoView({ block: "start" });
    const again = window.setTimeout(() => target()?.scrollIntoView({ block: "start" }), 350);
    return () => window.clearTimeout(again);
  }, [focusStepId, focusTick]);

  const hasSections = sections.length > 0; // the pane only exists with sections
  // With every step open the current step follows the scrolling: the last one whose head has passed the top of the pane.
  const activateRef = useRef(onActivateStep);
  activateRef.current = onActivateStep;
  const currentRef = useRef(openStepId);
  currentRef.current = openStepId;
  useEffect(() => {
    // During a search the shown steps are few and the current one stays shown for its unsaved typing: scrolling must not move it.
    if (!allOpen || query.trim() !== "") return;
    let pane: HTMLElement | null = scrollRef.current?.parentElement ?? null;
    while (pane && !/(auto|scroll)/.test(getComputedStyle(pane).overflowY)) pane = pane.parentElement;
    if (!pane) return;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (Date.now() - jumpedAt.current < 1000) return;
        const top = pane.getBoundingClientRect().top + 96;
        let id: number | null = null;
        for (const el of scrollRef.current?.querySelectorAll<HTMLElement>("[data-step-id]") ?? []) {
          if (el.getClientRects().length === 0) continue; // hidden by a search
          if (el.getBoundingClientRect().top > top) break;
          id = Number(el.dataset.stepId);
        }
        // At the very bottom a short last step can never reach the top: then it is the current one.
        if (pane.scrollTop + pane.clientHeight >= pane.scrollHeight - 4) {
          const shown = [...(scrollRef.current?.querySelectorAll<HTMLElement>("[data-step-id]") ?? [])].filter((el) => el.getClientRects().length > 0);
          const last = shown[shown.length - 1];
          if (last) id = Number(last.dataset.stepId);
        }
        const first = scrollRef.current?.querySelector<HTMLElement>("[data-step-id]");
        id ??= first ? Number(first.dataset.stepId) : null;
        if (id != null && id !== currentRef.current) activateRef.current(id);
      });
    };
    pane.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      pane.removeEventListener("scroll", onScroll);
    };
  }, [allOpen, hasSections, query]);

  if (sections.length === 0) {
    // Over the 3D view it gets the steps' frosted panel, or the line is unreadable on the scene.
    return (
      <p className={cn("text-muted-foreground p-6 text-sm", besideUnity && "bg-background/80 pointer-events-auto m-3 w-fit rounded-xl shadow-lg backdrop-blur-xl")}>
        {t("manualEditor.noSectionsYet")}
      </p>
    );
  }

  const q = query.trim().toLowerCase();
  // Inserting needs the whole list: a search shows a subset, so a position in it says nothing.
  const canInsert = !readOnly && q === "";
  const commitStep = (name: string) => {
    const trimmed = name.trim();
    const target = addingStep;
    setAddingStep(null);
    if (!trimmed || !target) return;
    void createStepAt(gate, m, { sectionId: target.sectionId, name: trimmed, beforeId: target.beforeId }).then((id) => id !== undefined && onOpenStep(id));
  };
  const commitSection = (name: string) => {
    const trimmed = name.trim();
    const target = addingSection;
    setAddingSection(null);
    if (!trimmed || !target) return;
    createSectionAt(gate, m, { name: trimmed, beforeId: target.beforeId });
  };
  const commitRename = (sectionId: number, name: string, previous: string) => {
    const trimmed = name.trim();
    setRenamingSection(null);
    if (!trimmed || trimmed === previous) return;
    void gate.enqueue(m.renameSection, (expectedVersion) => ({ expectedVersion, sectionId, name: trimmed })).catch(() => {});
  };
  const nameField = (placeholder: string, onCommit: (name: string) => void, onCancel: () => void) => (
    <div className="flex max-w-sm">
      <RenameInput initial="" placeholder={placeholder} onCommit={onCommit} onCancel={onCancel} />
    </div>
  );

  return (
    <div
      ref={scrollRef}
      data-steps-column
      className={cn(
        "flex flex-col gap-2 p-6 max-sm:px-3 max-sm:py-4",
        // Over the 3D view a frosted panel: headings stay readable on any scene, and the wheel over it scrolls the steps
        // instead of falling through a gap between two cards to Unity's zoom (design-notes "input routing").
        besideUnity
          ? "animate-in fade-in-0 slide-in-from-left-4 pointer-events-auto m-3 max-w-2xl rounded-2xl border border-white/50 bg-background/75 shadow-xl backdrop-blur-xl backdrop-saturate-150 duration-300 motion-reduce:animate-none dark:border-white/10 dark:bg-background/70"
          : "mx-auto max-w-[1200px]",
      )}
    >
      {besideUnity && onHideSteps ? (
        // The panel's own head: hiding the steps is something done TO this panel, so the control lives on it.
        <div className="bg-background/70 sticky top-0 z-20 -mx-6 -mt-6 mb-1 flex items-center justify-end gap-3 rounded-t-2xl px-4 py-2 backdrop-blur-xl max-sm:-mx-3 max-sm:-mt-4 dark:bg-background/60">
          {/* One short line, so the control is found and its point is clear (owner 2026-11-04); grey, the button stays the cue. */}
          <p className="text-muted-foreground m-0 mr-auto min-w-0 truncate text-xs">{t("manualEditor.positionHint")}</p>
          <Button variant="outline" size="sm" aria-pressed={false} onPress={onHideSteps} className="dark:border-border gap-1.5">
            <Move aria-hidden="true" />
            {t("manualEditor.hideSteps")}
          </Button>
        </div>
      ) : null}
      {sections.map((section) => {
        const nameHit = section.name.toLowerCase().includes(q);
        const hits = stepsForQuery(section, query, openStepId);
        const hideSection = q !== "" && !nameHit && hits.length === 0;
        // With every step open the cards hold unsaved typing and search must not unmount them: it only hides what does not match.
        const steps = allOpen ? section.steps : hits;
        const hiddenSteps = new Set(allOpen ? section.steps.filter((st) => !hits.includes(st)).map((st) => st.id) : []);
        if (hideSection && !allOpen) return null;
        return (
          <div key={section.id} className={hideSection ? "hidden" : "group/section contents"}>
            {/* Directly above a heading the line adds a SECTION; directly under a section's last step it adds a step. */}
            {canInsert && addingSection?.beforeId === section.id ? nameField(t("manualEditor.sectionName"), commitSection, () => setAddingSection(null)) : null}
            <div data-section-head
              className={cn("relative flex min-h-8 items-center gap-2", section.id !== sections[0]?.id && "mt-5")}>
              {canInsert && addingSection?.beforeId !== section.id && section.id !== sections[0]?.id ? (
                <InsertSlot text={t("manualEditor.insertSection")} label={t("manualEditor.insertSectionBefore", { name: section.name })} onPress={() => setAddingSection({ beforeId: section.id })} className="absolute inset-x-0 -top-3 h-3" buttonClassName="h-4" />
              ) : null}
              {renamingSection === section.id ? (
                <RenameInput initial={section.name} onCommit={(v) => commitRename(section.id, v, section.name)} onCancel={() => setRenamingSection(null)} />
              ) : (
                <h2 className="text-muted-foreground m-0 min-w-0 truncate text-xs font-semibold tracking-wide uppercase">{section.name}</h2>
              )}
              <span className="text-muted-foreground shrink-0 text-xs">{t("manualEditor.countSteps", { count: section.steps.length })}</span>
              {!readOnly ? (
                <span className="ml-auto">
                  <SectionActionsMenu
                    section={section}
                    sections={sections}
                    gate={gate}
                    m={m}
                    onRename={() => setRenamingSection(section.id)}
                    onInsert={canInsert ? (beforeId) => setAddingSection({ beforeId }) : undefined}
                  />
                </span>
              ) : null}
            </div>
            {steps.length === 0 && q === "" ? <p className="text-muted-foreground m-0 text-sm">{t("manualEditor.noStepsInSection")}</p> : null}
            {steps.map((st) => {
              const menu = readOnly ? null : (focusName: (() => void) | null) => (
                <StepActionsMenu
                  step={st}
                  section={section}
                  sections={sections}
                  gate={gate}
                  m={m}
                  onRename={() => {
                    if (focusName) focusName();
                    else {
                      setRenameOnOpen(st.id);
                      onOpenStep(st.id);
                    }
                  }}
                  onInsert={canInsert ? (beforeId) => setAddingStep({ sectionId: section.id, beforeId }) : undefined}
                />
              );
              return (
                <Fragment key={`${reloadKey}-${st.id}`}>
                  {canInsert && addingStep?.beforeId === st.id ? nameField(t("manualEditor.stepName"), commitStep, () => setAddingStep(null)) : null}
                  <StepDropZone step={st} readOnly={readOnly} isArManual={isArManual} onDrop={onDropOnStep} hidden={hiddenSteps.has(st.id)}>
                    {canInsert && addingStep?.beforeId !== st.id ? (
                      <InsertSlot label={t("manualEditor.insertStepBefore", { name: st.name })} onPress={() => setAddingStep({ sectionId: section.id, beforeId: st.id })} className="absolute inset-x-0 -top-3 -ml-5 h-4 max-sm:-ml-2.5" buttonClassName="h-4" />
                    ) : null}
                    {allOpen || st.id === openStepId ? (
                      <LazyMount active={allOpen && st.id !== openStepId} placeholder={<StepRow step={st} onOpen={() => onOpenStep(st.id)} menu={null} />}>
                      <div className="contents" onFocusCapture={allOpen ? () => st.id !== openStepId && onActivateStep(st.id) : undefined}>
                      <StepCard
                        step={st}
                        autoFocusName={renameOnOpen === st.id}
                        current={st.id === openStepId}
                        onNameFocused={() => setRenameOnOpen(null)}
                        renderMenu={(focusName) => (
                          <span className="flex items-center gap-0.5">
                            {allOpen ? null : (
                              <TooltipTrigger delay={300} focusable={false}>
                                <Button variant="ghost" size="icon-sm" aria-label={t("manualEditor.collapseStep")} onPress={onCloseStep}>
                                  <ChevronUp aria-hidden="true" className="size-4" />
                                </Button>
                                <Tooltip>{t("manualEditor.collapseStep")}</Tooltip>
                              </TooltipTrigger>
                            )}
                            {menu ? menu(focusName) : null}
                          </span>
                        )}
                        gate={gate}
                        disabled={readOnly}
                        m={m}
                        session={session}
                        manualId={manualId}
                        onPickMain={onPickMain}
                        onAddElement={onAddElement}
                        onRemoveElement={onRemoveElement}
                        onDuplicateElement={onDuplicateElement}
                        onSetSetting={onSetSetting}
                        onPickSettingFile={onPickSettingFile}
                        copiedFrom={copiedFrom}
                        onCopySettings={onCopySettings}
                        onPasteSettings={onPasteSettings}
                        isArManual={isArManual}
                        job={jobByStep.get(st.id)}
                        initialStateSensorIds={initialStateSteps.get(st.id)}
                        registerFlush={registerFlush}
                        unregisterFlush={unregisterFlush}
                        isRefreshing={isRefreshing}
                        onPickMedia={onPickMedia ? () => onPickMedia(st.id) : undefined}
                        onDetachMedia={onDetachMedia ? () => onDetachMedia(st.id) : undefined}
                        onElementSelected={onElementSelected}
                      />
                      </div>
                      </LazyMount>
                    ) : (
                      <StepRow step={st} onOpen={() => onOpenStep(st.id)} menu={menu ? menu(null) : null} />
                    )}
                  </StepDropZone>
                </Fragment>
              );
            })}
            {canInsert ? (
              addingStep?.sectionId === section.id && addingStep.beforeId === undefined ? (
                nameField(t("manualEditor.stepName"), commitStep, () => setAddingStep(null))
              ) : steps.length > 0 ? (
                <Button variant="ghost" size="sm" aria-label={t("manualEditor.insertStepAtEnd")} onPress={() => setAddingStep({ sectionId: section.id })} className="text-muted-foreground hover:text-foreground -mt-1 w-fit gap-1.5 opacity-0 transition-opacity duration-150 group-hover/section:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100 motion-reduce:transition-none">
                  {/* Under every section at rest it repeated down the page: it shows where the pointer is (touch keeps it). */}
                  <Plus aria-hidden="true" />
                  {t("manualEditor.addStep")}
                </Button>
              ) : (
                <Button variant="outline" size="sm" onPress={() => setAddingStep({ sectionId: section.id })} className="dark:border-border w-fit gap-1.5">
                  {t("manualEditor.addStep")}
                </Button>
              )
            ) : null}
          </div>
        );
      })}
      {canInsert ? (
        addingSection && addingSection.beforeId === undefined ? (
          nameField(t("manualEditor.sectionName"), commitSection, () => setAddingSection(null))
        ) : (
          <button
            type="button"
            onClick={() => setAddingSection({})}
            className="border-border text-muted-foreground hover:border-ring/60 hover:text-foreground focus-visible:ring-primary mt-3 cursor-pointer rounded-xl border border-dashed bg-transparent p-2 text-center text-sm outline-none focus-visible:ring-2"
          >
            {t("manualEditor.addSection")}
          </button>
        )
      ) : null}
    </div>
  );
}
